import React, { FC, Fragment, useState, MouseEvent } from 'react';
import { formatDate } from 'utils/format';
import { Box, Button, CircularProgress, IconButton, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ApolloError, useMutation } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { OrderForm, OrderFormStatus } from 'models/order-form';
import OrderFormDialog from './order-form-dialog/order-form-dialog.component';
import { useNavigate } from 'react-router-dom';

import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import DeleteIcon from '@mui/icons-material/Delete';
import { deleteOrderFormTemplate } from 'apollo/templates/order-form';

type OrderFormListProps = {
    forms: OrderForm[];
    onDelete: () => void;
};
const OrderFormList: FC<OrderFormListProps> = ({ forms, onDelete }: OrderFormListProps) => {
    const { t: translate } = useTranslation(['order-forms', 'common']);

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [currentOrderForm, setCurrentOrderForm] = useState<OrderForm>();
    const [isDeleteDialogOpen, setDeleteIsDialogOpen] = useState(false);
    const [deleteFormId, setDeleteFormId] = useState<string>();
    const { showBoundary } = useDenthubErrorBoundary();
    const navigate = useNavigate();

    const [deleteOrderForm] = useMutation(deleteOrderFormTemplate, {
        onCompleted: () => {
            onDelete();
        },
        onError: (error: ApolloError) => {
            showBoundary(error);
        },
    });

    const handleRowClick = (form: OrderForm) => {
        if (form.status === OrderFormStatus.READY || form.status === OrderFormStatus.ORDER_CREATED) {
            setIsDialogOpen(true);
            setCurrentOrderForm(form);
        }
    };

    const createDenthubOrder = (event: MouseEvent, form: OrderForm) => {
        event.preventDefault();
        event.stopPropagation();
        navigate(`/orders/create-from-order-form/${form._id}`);
    };

    const handleDeleteClick = (event: MouseEvent, formId: string) => {
        event.stopPropagation();
        setDeleteFormId(formId);
        setDeleteIsDialogOpen(true);
    };

    const handleConfirmDelete = () => {
        deleteOrderForm({ variables: { orderFormId: deleteFormId } });
        setDeleteIsDialogOpen(false);
    };

    const colorMap = {
        [OrderFormStatus.CREATED]: 'ai.main',
        [OrderFormStatus.PROCESSING]: 'ai.main',
        [OrderFormStatus.READY]: 'success.main',
        [OrderFormStatus.ERROR]: 'error.dark',
        [OrderFormStatus.ORDER_CREATED]: 'info.main',
    };

    return (
        <Fragment>
            <TableBody>
                {forms.map((form) => (
                    <TableRow key={form._id} onClick={() => handleRowClick(form)} hover sx={{ cursor: 'pointer' }}>
                        <TableCell>
                            <Box display="flex" alignItems="center" sx={{ width: '100px' }}>
                                {form.status === OrderFormStatus.CREATED ||
                                form.status === OrderFormStatus.PROCESSING ? (
                                    <CircularProgress size={13} sx={{ marginRight: 0.5, color: 'ai.main' }} />
                                ) : undefined}
                                <Typography
                                    variant="body2"
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        fontWeight: 600,
                                        color: colorMap[form.status],
                                    }}
                                >
                                    {translate(`order-forms:ORDER_FORM_STATUS_${form.status}`)}
                                </Typography>
                            </Box>
                        </TableCell>
                        <TableCell>
                            {form.status === OrderFormStatus.CREATED || form.status === OrderFormStatus.PROCESSING ? (
                                <Box sx={{ color: 'ai.main', whiteSpace: 'pre-line' }}>
                                    {translate(`order-forms:AI_PROCESSING_ORDER_FORM`)}
                                </Box>
                            ) : (
                                <Box>{form.title}</Box>
                            )}
                        </TableCell>
                        <TableCell>{formatDate(form.createdAt, 'datetime')}</TableCell>
                        <TableCell>{form.createdBy?.fullName}</TableCell>
                        <TableCell align="right">
                            <IconButton
                                disabled={
                                    form.status !== OrderFormStatus.READY && form.status !== OrderFormStatus.ERROR
                                }
                                onClick={(event) => handleDeleteClick(event, form._id as string)}
                                size="small"
                            >
                                <DeleteIcon />
                            </IconButton>
                            <Button
                                disabled={form.status !== OrderFormStatus.READY}
                                onClick={(event) => createDenthubOrder(event, form)}
                            >
                                {translate('order-forms:CREATE_DENTHUB_ORDER')}
                            </Button>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>

            <OrderFormDialog
                open={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                partialOrderForm={currentOrderForm}
            />
            <ConfirmDialog
                open={isDeleteDialogOpen || false}
                handleClose={() => setDeleteIsDialogOpen(false)}
                dialogTitle={translate('order-forms:DELETE_ORDER_FORM')}
                dialogContent={translate('order-forms:CONFIRM_DELETE_ORDER_FORM')}
                handleConfirm={handleConfirmDelete}
            />
        </Fragment>
    );
};

export default OrderFormList;
